// エンコーダ（ffmpeg-core）を API から取得し、版ごとに Cache Storage へ保存して Blob URL を返す
// 中身は ENCODER_CORE の SHA-256 で検証する（どの PC でも同じエンコーダで変換するため）
/* global globalThis */
import { fetchOpsEncoderAsset } from "apis/AdminOpsEncoderAssetApi";
import { ENCODER_CORE } from './encodeSpec';
import { OptimizeError } from './videoOptimize';

const CACHE_NAME = 'prizmo-encoder-core';

const sha256Hex = async (buffer) => Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', buffer)))
    .map((b) => b.toString(16).padStart(2, '0')).join('');

const cacheKeyOf = (name) => `/__prizmo_encoder__/${ENCODER_CORE.version}/${name}`;

const openCache = async (cacheStorage) => {
    try {
        return cacheStorage ? await cacheStorage.open(CACHE_NAME) : null;
    } catch (e) {
        // プライベートウィンドウ等で使えないときは毎回取得する
        return null;
    }
};

/**
 * @returns {Promise<{ coreURL: string, wasmURL: string }>}
 */
export const loadEncoderAssets = async ({
    fetchAsset = fetchOpsEncoderAsset,
    cacheStorage = globalThis.caches,
    digest = sha256Hex,
    createObjectURL = (blob) => URL.createObjectURL(blob),
    toResponse = (buffer, type) => new Response(buffer, { headers: { 'Content-Type': type } }),
} = {}) => {
    const cache = await openCache(cacheStorage);

    const load = async ({ name, sha256, type }) => {
        const key = cacheKeyOf(name);
        if (cache) {
            const hit = await cache.match(key);
            if (hit) {
                const buffer = await hit.arrayBuffer();
                if (await digest(buffer) === sha256) return new Blob([buffer], { type });
                await cache.delete(key);
            }
        }
        let buffer;
        try {
            buffer = await fetchAsset(ENCODER_CORE.version, name);
        } catch (e) {
            throw new OptimizeError('encoder_unavailable', e.message);
        }
        if (await digest(buffer) !== sha256) throw new OptimizeError('encoder_unavailable', `${name} hash mismatch`);
        if (cache) await cache.put(key, toResponse(buffer, type)).catch(() => {});
        return new Blob([buffer], { type });
    };

    const core = await load(ENCODER_CORE.files.core);
    const wasm = await load(ENCODER_CORE.files.wasm);
    return { coreURL: createObjectURL(core), wasmURL: createObjectURL(wasm) };
};
